// src/components/lesson/ConceptMap.tsx
//
// Per-lesson "you are here" map. Shows the current topic in the middle
// flanked by prerequisites on the left and next-up topics on the right.
// Click any node to navigate. SVG-based, no force-directed layout
// needed for this simple structure.
//
// Why per-lesson and not global: a global concept map (all 80 SAT
// topics) is great for a sitemap-style overview, but most students
// looking at a lesson want to know "what comes before / after THIS
// topic". For a lesson on the learning path the caller passes the path
// graph's links (conceptMapLinks in src/lib/learn/learning-path.ts), so
// "Next" here agrees with the path bar and the placement verdict and is
// never a prerequisite; the lesson's own generated prerequisites/nextTopics
// are only a fallback for a lesson off the path.

'use client'

import Link from 'next/link'

export type ConceptMapNode = {
  topic: string
  slug: string
}

export default function ConceptMap({
  current,
  prerequisites = [],
  nextTopics = [],
}: {
  current: { topic: string; slug?: string }
  prerequisites?: ConceptMapNode[]
  nextTopics?: ConceptMapNode[]
}) {
  // Don't render if there's nothing connected — the lesson stands alone.
  if (prerequisites.length === 0 && nextTopics.length === 0) return null

  const totalLeft = prerequisites.length
  const totalRight = nextTopics.length

  return (
    <figure
      role="img"
      aria-label={`Concept map for ${current.topic}`}
      className="my-3 overflow-x-auto rounded-md border border-border bg-card/60 p-4"
    >
      <div className="mb-3 text-xs font-medium uppercase tracking-wide text-muted-foreground">
        Where this lesson fits
      </div>

      <div className="grid grid-cols-[1fr,auto,1fr] gap-4">
        {/* Prerequisites column */}
        <div className="space-y-2">
          {totalLeft > 0 && (
            <div className="mb-1 text-right text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
              Before
            </div>
          )}
          {prerequisites.map((p) => (
            <NodeBox key={p.slug} node={p} side="left" />
          ))}
        </div>

        {/* Center: connector + current node */}
        <div className="flex flex-col items-center justify-center gap-2">
          {/* Top connector — only shows if there are prereqs */}
          {totalLeft > 0 || totalRight > 0 ? (
            <ConnectorRow hasLeft={totalLeft > 0} hasRight={totalRight > 0} />
          ) : null}
          <div className="rounded-lg border-2 border-brand/60 bg-brand/15 px-4 py-2 text-center text-sm font-semibold text-brand-ink shadow-sm shadow-brand/20">
            {current.topic}
          </div>
          <div className="text-[10px] text-brand-ink/70">You are here</div>
        </div>

        {/* Next topics column */}
        <div className="space-y-2">
          {totalRight > 0 && (
            <div className="mb-1 text-left text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
              Next
            </div>
          )}
          {nextTopics.map((n) => (
            <NodeBox key={n.slug} node={n} side="right" />
          ))}
        </div>
      </div>
    </figure>
  )
}

function NodeBox({ node, side }: { node: ConceptMapNode; side: 'left' | 'right' }) {
  return (
    <Link
      href={`/learn/${node.slug}`}
      className={
        'group flex items-center gap-2 rounded-md border border-border bg-card/40 px-3 py-2 text-sm text-foreground transition-colors hover:border-brand/40 hover:bg-brand/10 ' +
        (side === 'left' ? 'justify-end text-right' : 'justify-start')
      }
    >
      {side === 'right' && (
        <span className="text-muted-foreground transition-colors group-hover:text-brand-ink">
          →
        </span>
      )}
      <span className="flex-1 truncate">{node.topic}</span>
      {side === 'left' && (
        <span className="text-muted-foreground transition-colors group-hover:text-brand-ink">
          →
        </span>
      )}
    </Link>
  )
}

function ConnectorRow({ hasLeft, hasRight }: { hasLeft: boolean; hasRight: boolean }) {
  // Visual connector — short horizontal lines that hint at the
  // bidirectional flow. Pure decoration; doesn't affect layout much.
  return (
    <div className="flex w-full items-center gap-1">
      <div className={'h-px flex-1 ' + (hasLeft ? 'bg-slate-700' : 'bg-transparent')} />
      <div className={'h-px flex-1 ' + (hasRight ? 'bg-slate-700' : 'bg-transparent')} />
    </div>
  )
}
